// Angular
import { Pipe, PipeTransform, inject } from '@angular/core';
// Transloco
import { TranslocoService } from '@jsverse/transloco';

@Pipe({
    name: 'timeAgo',
    pure: true
})
export class TimeAgoPipe implements PipeTransform {

    private readonly translocoService = inject(TranslocoService);

    /**
     * Time Ago Pipe Transform
     * @param {(string | Date | null | undefined)} value
     * @return {string}
     * @memberof TimeAgoPipe
     */
    transform(value: string | Date | null | undefined): string {
        if (!value) return '--';

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '--';

        const days = this.getCalendarDayDifference(date);
        if (days === 0) return this.translocoService.translate('label_today');
        if (days === -1) return this.translocoService.translate('label_yesterday');
        if (Math.abs(days) < 30) return this.translocoService.translate('dynamic_days_ago', { count: Math.abs(days) });

        const months = this.getCalendarMonthDifference(date);
        if (months === -1) return this.translocoService.translate('label_last_month');
        if (Math.abs(months) < 12) return this.translocoService.translate('dynamic_months_ago', { count: Math.abs(months) });

        const years = this.getCalendarYearDifference(date);
        if (years === -1) return this.translocoService.translate('label_last_year');
        return this.translocoService.translate('dynamic_years_ago', { count: Math.abs(years) });
    }

    /**
     * Calculates the difference between calendar dates rather than elapsed milliseconds.
     * Sep 08 -> Sep 08 = 0
     * Sep 07 -> Sep 08 = -1
     * Sep 06 -> Sep 08 = -2
     */
    private getCalendarDayDifference(date: Date): number {
        const today = new Date();
        const currentDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
        const targetDay = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
        return Math.round((targetDay - currentDay) / 86_400_000); // 86_400_000 = milliseconds in a day
    }

    /**
     * Calendar Month Difference
     * @param {Date} date
     * @return {number}
     * @memberof TimeAgoPipe
     */
    private getCalendarMonthDifference(date: Date): number {
        const today = new Date();
        return ((date.getFullYear() - today.getFullYear()) * 12 + (date.getMonth() - today.getMonth()));
    }

    /**
     * Calendar Year Difference
     * @param {Date} date
     * @return {number}
     * @memberof TimeAgoPipe
     */
    private getCalendarYearDifference(date: Date): number {
        return date.getFullYear() - new Date().getFullYear();
    }
}